import {
  hasPermission,
  isKlearRole,
  type KlearRole,
  ROLE_KLEAR_CSM,
  ROLE_KLEAR_FUNDER,
  ROLE_KLEAR_SUPER_ADMIN,
  ROLE_TRADER_ORGANIZATION_OWNER
} from '@klear/constants'
import { Timestamp } from '@klear/time'
import isObject from 'lodash/isObject'

import type { UserRole } from '@klear/user/user-role'

export type UserIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export function userToJson(user: User) {
  return {
    identity: user.identity,
    email: user.email,
    firstName: user.firstName,
    middleName: user.middleName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber,
    jobTitle: user.jobTitle,
    lastLoginTime: user.lastLoginTime?.epochMs,
    roles: user.roles.map((r) => ({ ...r })),
    createdAt: user.createdAt.epochMs,
    updatedAt: user.updatedAt.epochMs,
    deletedAt: user.deletedAt?.epochMs,
    createdByUserId: user.createdByUserId,
    createdByUserEmail: user.createdByUserEmail,
    updatedByUserId: user.updatedByUserId,
    updatedByUserEmail: user.updatedByUserEmail,
    deletedByUserId: user.deletedByUserId,
    deletedByUserEmail: user.deletedByUserEmail,
    welcomeEmailSent: user.welcomeEmailSent,
    welcomeEmailSentAt: user.welcomeEmailSentAt?.epochMs,
    accountCreatedAt: user.accountCreatedAt?.epochMs,
    firstLoginAt: user.firstLoginAt?.epochMs,
    lastLoginAt: user.lastLoginAt?.epochMs,
    hasLoggedIn: user.hasLoggedIn,
    termsOfServiceAccepted: user.termsOfServiceAccepted,
    privacyPolicyAccepted: user.privacyPolicyAccepted
  }
}
export type UserJson = ReturnType<typeof userToJson>

export class User {
  static readonly __klearClass = 'User'

  readonly identity: UserIdentity
  email: string
  firstName: string
  middleName: string
  lastName: string
  phoneNumber?: string
  jobTitle: string
  lastLoginTime?: Timestamp
  roles: UserRole[]
  createdAt: Timestamp
  updatedAt: Timestamp
  deletedAt?: Timestamp
  createdByUserId?: number
  createdByUserEmail?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  welcomeEmailSent?: boolean
  welcomeEmailSentAt?: Timestamp
  accountCreatedAt?: Timestamp
  firstLoginAt?: Timestamp
  lastLoginAt?: Timestamp
  hasLoggedIn?: boolean
  termsOfServiceAccepted?: boolean
  privacyPolicyAccepted?: boolean

  constructor(props: Partial<UserJson>) {
    const now = Timestamp.now()
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.email = (props.email ?? '').toLowerCase()
    this.firstName = props.firstName ?? ''
    this.middleName = props.middleName ?? ''
    this.lastName = props.lastName ?? ''
    this.phoneNumber = props.phoneNumber || undefined
    this.jobTitle = props.jobTitle ?? ''
    this.lastLoginTime = props.lastLoginTime ? new Timestamp(props.lastLoginTime) : undefined
    this.roles = (props.roles ?? []).map((r) => ({ ...r }))
    this.createdAt = new Timestamp(props.createdAt ?? now)
    this.updatedAt = props.updatedAt ? new Timestamp(props.updatedAt) : this.createdAt
    this.deletedAt = props.deletedAt ? new Timestamp(props.deletedAt) : undefined
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail

    this.welcomeEmailSent = props.welcomeEmailSent
    this.welcomeEmailSentAt = props.welcomeEmailSentAt ? new Timestamp(props.welcomeEmailSentAt) : undefined
    this.accountCreatedAt = props.accountCreatedAt ? new Timestamp(props.accountCreatedAt) : undefined
    this.firstLoginAt = props.firstLoginAt ? new Timestamp(props.firstLoginAt) : undefined
    this.lastLoginAt = props.lastLoginAt ? new Timestamp(props.lastLoginAt) : undefined
    this.hasLoggedIn = props.hasLoggedIn
    this.termsOfServiceAccepted = props.termsOfServiceAccepted
    this.privacyPolicyAccepted = props.privacyPolicyAccepted

    this.assertValid()
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  get fullName(): string {
    return [this.firstName, this.middleName, this.lastName].filter((part) => part.trim()).join(' ')
  }

  get staffRoles(): KlearRole[] {
    return this.roles.filter((r) => isKlearRole(r.role)).map((r) => r.role as KlearRole)
  }

  get traderRoles(): UserRole[] {
    return this.roles.filter((r) => !isKlearRole(r.role))
  }

  get hasKlearRole(): boolean {
    return this.staffRoles.length > 0
  }

  get isSuperAdmin(): boolean {
    return this.roles.some((r) => r.role === ROLE_KLEAR_SUPER_ADMIN)
  }

  get isCSM(): boolean {
    return this.roles.some((r) => r.role === ROLE_KLEAR_CSM)
  }

  get isFunder(): boolean {
    return this.roles.some((r) => r.role === ROLE_KLEAR_FUNDER)
  }

  get traderIds(): number[] {
    return [...new Set(this.roles.map((r) => r.traderId))]
  }

  hasRole(role: string): boolean {
    return this.roles.some((r) => r.role === role)
  }

  hasOrganizationOwnerRoleForTrader(traderId: number): boolean {
    return this.roles.some((r) => r.traderId === traderId && r.role === ROLE_TRADER_ORGANIZATION_OWNER)
  }

  hasPermission(entity: string | undefined, action: string | undefined): boolean {
    if (!entity || !action) {
      return false
    }
    return this.roles.some((r) => hasPermission(r.role, entity, action))
  }

  hasStaffPermission(entity: string | undefined, action: string | undefined): boolean {
    if (!entity || !action) {
      return false
    }
    return this.staffRoles.some((role) => hasPermission(role, entity, action))
  }

  hasTraderPermission(traderId: number, entity: string | undefined, action: string | undefined): boolean {
    if (!entity || !action) {
      return false
    }
    return this.traderRoles.some((r) => r.traderId === traderId && hasPermission(r.role, entity, action))
  }

  requireSaved(): asserts this is this & { id: number } {
    if (!this.isSaved) {
      throw new Error('User is not saved')
    }
  }

  assertValid(): void {
    if (!this.email.trim()) {
      throw new Error('User email is required')
    }
  }

  toJSON() {
    return userToJson(this)
  }

  static fromSession(raw: unknown): User | undefined {
    if (!isObject(raw) || !('identity' in raw)) {
      return undefined
    }
    try {
      return new User(raw as Partial<UserJson>)
    } catch {
      return undefined
    }
  }
}
